/**
 * Copyright ©2024 Drivepoint
 */

import {Config, State} from "@bainbridge-growth/node-frontend";
import LaunchDarklyClient from "./LaunchDarklyService.ts";

// SPIKE-BRANCH DEMO BRIDGE — deployed DEVELOPMENT environment ONLY. These four prototype flags
// do not exist in LaunchDarkly yet, so allFlags() never returns them and they read as undefined
// everywhere. A development build (vite.config.ts bakes "environment" from NODE_ENV; only
// build:development bakes "development") falls back to this map so the spike demo lights up —
// but ONLY while LaunchDarkly is silent: the moment a real flag exists, its value wins outright,
// including an explicit false. Staging/production never consult this map twice over: their baked
// environment differs, AND their deploys build from develop/main, which do not carry this commit.
// This commit is DROPPED when the branch is rebuilt at PR prep.
const SPIKE_DEVELOPMENT_DEFAULTS: Record<string, boolean> = {
  "excel.add.in.chat.tool.groups.v2.enabled": true,
  "excel.add.in.chat.sheet.grouping.enabled": true,
  "excel.add.in.chat.permission.gate.enabled": true,
  "excel.add.in.chat.scroll.v2.enabled": true
};

export default class FeatureFlagsService {

  private static _featureFlags: any = {};

  static async start(): Promise<void> {
    State.registerMany("user", "company", FeatureFlagsService.onUserStateChange);
    LaunchDarklyClient.client.on("change", FeatureFlagsService.refreshFeatureFlags);
    State.set("flags", FeatureFlagsService.featureFlags);
  }

  static async onUserStateChange(event: any): Promise<void> {
    await LaunchDarklyClient.identify(State.get("user"), State.get("company"));
    await FeatureFlagsService.refreshFeatureFlags();
  }

  static async refreshFeatureFlags(): Promise<void> {
    FeatureFlagsService.featureFlags = await LaunchDarklyClient.flags();
  }

  static get(key: string): any {
    // Dev-only runtime override (SpikeDevPanel). `import.meta.hot` is defined ONLY by the
    // Vite dev server and replaced with `undefined` in every `vite build` (local/development/
    // staging/production), so this block is dead-code-eliminated from all deployed bundles.
    // (import.meta.env.DEV is NOT safe: the build scripts export NODE_ENV=development/staging,
    // which makes DEV true in those deployed builds.) The MODE==="test" arm keeps the override
    // unit-testable under vitest.
    if (import.meta.hot || import.meta.env.MODE === "test") {
      const overrides = State.get("spike:flagOverrides") as Record<string, any> | undefined;
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
        return overrides[key];
      }
    }
    // LaunchDarkly always wins when it has the flag — an explicit false stays false.
    const value = FeatureFlagsService._featureFlags?.[key];
    if (value !== undefined) { return value; }
    // LaunchDarkly is silent. Development builds fall back to the spike defaults; for every
    // other key (and every other environment) this stays undefined, exactly as before.
    if (Config.get("environment") === "development" && Object.prototype.hasOwnProperty.call(SPIKE_DEVELOPMENT_DEFAULTS, key)) {
      return SPIKE_DEVELOPMENT_DEFAULTS[key];
    }
    return undefined;
  }

  static get featureFlags(): any {
    return FeatureFlagsService._featureFlags;
  }

  static set featureFlags(value: any) {
    FeatureFlagsService._featureFlags = value || {};
    State.set("flags", FeatureFlagsService._featureFlags);
  }

}
